import { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import type { PropertyFileTreeNode } from '@/shared/api';
import { collectFolderNodes } from './akteBrowserNav';
import { dialogInputClass, dialogLabelClass } from './akteDialogFields';
import { CATEGORY_ROOTS, isSystemFolder, segmentsOf } from './akteFolderRules';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tree: PropertyFileTreeNode;
  defaultParent: string;
  onCreate: (path: string) => Promise<void>;
}

/** Gültige Eltern-Ordner: Kategorie-Roots + Nicht-System-Unterordner. */
function parentOptions(tree: PropertyFileTreeNode): { path: string; label: string }[] {
  const roots = CATEGORY_ROOTS.map((p) => ({ path: p, label: p }));
  const subs = collectFolderNodes(tree)
    .filter(
      (n) => segmentsOf(n.path).length >= 2 && !isSystemFolder(n.path) && !n.name.startsWith('_'),
    )
    .map((n) => ({ path: n.path, label: n.path.split('/').join(' / ') }));
  return [...roots, ...subs];
}

/** „Neuer Ordner" — Custom-Ordner unter einer Kategorie anlegen (§4.8). */
export function NewFolderDialog({ open, onOpenChange, tree, defaultParent, onCreate }: Props) {
  const options = useMemo(() => parentOptions(tree), [tree]);
  const initialParent = options.some((o) => o.path === defaultParent)
    ? defaultParent
    : (options[0]?.path ?? '');
  const [parent, setParent] = useState(initialParent);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    const clean = name.trim();
    if (!clean || !parent) return;
    setBusy(true);
    try {
      await onCreate(`${parent}/${clean}`);
      setName('');
      onOpenChange(false);
    } catch {
      // Fehler-Toast kommt aus der Mutation; Dialog bleibt offen.
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Neuer Ordner</DialogTitle>
          <DialogDescription>Ordner liegen innerhalb einer Kategorie.</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div>
            <label htmlFor="folder-parent" className={dialogLabelClass}>
              Übergeordnet
            </label>
            <select
              id="folder-parent"
              value={parent}
              onChange={(e) => setParent(e.target.value)}
              className={dialogInputClass}
            >
              {options.map((o) => (
                <option key={o.path} value={o.path}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="folder-name" className={dialogLabelClass}>
              Ordnername
            </label>
            <input
              id="folder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              className={dialogInputClass}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Abbrechen
          </Button>
          <Button variant="brand" loading={busy} disabled={!name.trim()} onClick={submit}>
            Anlegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
